import React, {
  createContext,
  Dispatch,
  FunctionComponent, Reducer, useCallback, useEffect, useReducer, useRef,
} from 'react';
import { useDebounceCallback } from '@react-hook/debounce';
import { NextActionExecutionHistory } from '@liveperson/lpcontextschemalib';
import { CustomProviderProps } from '.';
import api from '../api';
import {
  policyDebugPropOptions,
  timeSeriesOptions,
  PolicyDebugPropOption, TimeSeriesOption,
  PolicyExecutionHistoryPaginatedResultSet,
} from '../components/debugger';
import { PolicyDebugProp, TimeSeries } from '../enums';
import { useAppState, useNotification } from '../hooks';

const MILLISECONDS_ONE_DAY: number = 24 * 60 * 60 * 1000;

const dayMap: { [key in TimeSeries]: number } = {
  [TimeSeries.DAYS_7]: 7,
  [TimeSeries.HOURS_24]: 1,
};

export interface DebuggerContextProps {
  term: string;
  prop: PolicyDebugProp;
  startDate: Date;
  endDate: Date;
  selectedPolicyDebugPropOption: PolicyDebugPropOption;
  selectedTimeSeriesOption: TimeSeriesOption;
  history: PolicyExecutionHistoryPaginatedResultSet;
  isLoading: boolean;
  noOfRecordsPerPage: number;
  pageNumber: number;
}

export interface PolicyLogsSearchTerms {
  term: string;
  prop: number;
  days: number;
  noOfRecordsPerPage: number;
  pageNumber: number;
}

export interface DebuggerContextVals {
  state: DebuggerContextProps;
  dispatch: Dispatch<Partial<DebuggerContextProps>>;
  refresh: () => void;
}

export const debuggerState = createContext({} as DebuggerContextVals);
const { Provider } = debuggerState;

const DebuggerProvider: FunctionComponent<
CustomProviderProps
> = ({ children }: CustomProviderProps) => {
  const { accountId } = useAppState();
  const { addNotification } = useNotification();

  const isMounted = useRef<boolean>(false);

  // record the latest search
  const latestSearch = useRef<PolicyLogsSearchTerms | null>(null);

  const reducer: Reducer<
  DebuggerContextProps, Partial<DebuggerContextProps>
  > = (prev: DebuggerContextProps, stateUpdates: Partial<DebuggerContextProps>) => {
    const newState = {
      ...prev,
      ...stateUpdates,
    };

    if (stateUpdates.term != null) {
      newState.term = stateUpdates.term?.trim();
    }
    return newState;
  };

  const [state, dispatch] = useReducer<
  Reducer<DebuggerContextProps, Partial<DebuggerContextProps>>>(reducer, {
    term: '',
    prop: PolicyDebugProp.CONVERSATION_ID,
    endDate: new Date(),
    startDate: new Date(new Date().getTime() - dayMap[TimeSeries.DAYS_7] * MILLISECONDS_ONE_DAY),
    selectedPolicyDebugPropOption: policyDebugPropOptions[0],
    selectedTimeSeriesOption: timeSeriesOptions[0],
    history: { docs: [] as Array<NextActionExecutionHistory> },
    isLoading: false,
    noOfRecordsPerPage: 20,
    pageNumber: 1,
  } as any);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const fetchHistory = useCallback(async (
    searchedDays, searchedTerm, searchedProp, searchedNoOfRecordsPerPage, searchedPageNumber,
  ) => {
    dispatch({ isLoading: true });
    const start = Date.now();
    const now = new Date();
    const then = new Date(now.getTime() - searchedDays * MILLISECONDS_ONE_DAY);

    latestSearch.current = {
      term: searchedTerm,
      days: searchedDays,
      prop: searchedProp,
      noOfRecordsPerPage: searchedNoOfRecordsPerPage,
      pageNumber: searchedPageNumber,
    };

    try {
      const hist = await api.ccsV2.getPolicyHistory(accountId, {
        term: searchedTerm,
        prop: searchedProp,
        startDate: then,
        endDate: now,
        noOfRecordsPerPage: searchedNoOfRecordsPerPage,
        pageNumber: searchedPageNumber,
      });

      if (isMounted.current && latestSearch.current) {
        // only update state if these results are returned for the
        // latest search
        const {
          term,
          days,
          prop,
          noOfRecordsPerPage,
          pageNumber,
        } = latestSearch.current;

        if (term === searchedTerm
          && days === searchedDays
          && prop === searchedProp
          && noOfRecordsPerPage === searchedNoOfRecordsPerPage
          && pageNumber === searchedPageNumber
        ) {
          dispatch({
            isLoading: false,
            history: hist as PolicyExecutionHistoryPaginatedResultSet,
          });
        }
      }
    } catch (error) {
      if (isMounted.current) {
        addNotification(error, 'error');
        dispatch({
          history: {
            docs: [] as Array<NextActionExecutionHistory>,
            hasNextPage: false,
            hasPrevPage: false,
            limit: searchedNoOfRecordsPerPage,
            meta: null,
            nextPage: null,
            page: 1,
            pagingCounter: 0,
            prevPage: null,
            totalDocs: 0,
            totalPages: 1,
          } as any,
          isLoading: false,
        });
      }
    }
  }, [accountId, addNotification]);

  const debouncedSearch = useDebounceCallback(
    fetchHistory, 250, true,
  );

  const doDebouncedSearch = useCallback(() => {
    const days: number = dayMap[state.selectedTimeSeriesOption.value];
    const searchedTerm = state.term;
    const searchedProp = state.selectedPolicyDebugPropOption.value;
    const searchedNoOfRecordsPerPage = state.noOfRecordsPerPage;
    const searchedPageNumber = state.pageNumber;
    debouncedSearch(
      days, searchedTerm, searchedProp, searchedNoOfRecordsPerPage, searchedPageNumber,
    );
  }, [debouncedSearch, state.noOfRecordsPerPage, state.pageNumber,
    state.selectedPolicyDebugPropOption.value, state.selectedTimeSeriesOption.value, state.term]);

  useEffect(() => {
    doDebouncedSearch();
  }, [state.selectedTimeSeriesOption.value, accountId, state.term,
    state.selectedPolicyDebugPropOption.value, state.noOfRecordsPerPage, state.pageNumber,
    addNotification, debouncedSearch, doDebouncedSearch]);

  return (
    <Provider value={{
      state,
      dispatch,
      refresh: doDebouncedSearch,
    }}
    >
      {children}
    </Provider>
  );
};

export default DebuggerProvider;
